import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { addMixPanelEvent } from '../../store/actions';
import { trackGA4Event, trackGA4EventAsync } from '../../utils/trackGA4Event';
import { ONBOARDING_STEP_COMPLETED, ONBOARDING_COMPLETED } from '../../constants/gtag-events';
import { onboardingStartedAtKey, onboardingUseCaseKey } from '../../utils/onboardingSession';
import { API } from '../../utils/amplifyWrapper';
import Spinner from '../../components/Spinner';
import logo from "../../assets/logo-full.png";
import CustomURL from '../../utils/CustomURL';
import { useAuthStore } from '../../zustand-store/useAuthStore';

const audioForList = ["Audio Ads", "E-Learning", "Audio Book", "Presentation", "Social Media", "Announcement", "Dubbing / Translation", "Video", "Podcasts", "Apps / Games", "Personal Listening", "Articles / Blogs"];

const tagBaseStyle = {
    height: 50, border: '1px solid var(--bs-border-color)', borderRadius: 8,
    padding: 15, color: 'var(--bs-body-color)', lineHeight: '18px',
    cursor: 'pointer', transition: 'background 0.15s', userSelect: 'none',
    whiteSpace: 'nowrap',
};

function TagPill({ label, selected, onClick }) {
    const [hovered, setHovered] = useState(false);
    return (
        <div
            style={{
                ...tagBaseStyle,
                backgroundColor: selected
                    ? (hovered ? 'rgba(var(--bs-primary-rgb), 0.16)' : 'rgba(var(--bs-primary-rgb), 0.12)')
                    : (hovered ? 'rgba(var(--bs-primary-rgb), 0.04)' : 'transparent'),
                borderColor: selected ? 'var(--bs-primary)' : 'var(--bs-border-color)',
            }}
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            {label}
        </div>
    );
}

export default function OnboardingAudio() {
    const [audioFor, setAudioFor] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const history = useHistory();
    const dispatch = useDispatch();
    const { setAuthState } = useAuthStore();

    const urlParams = new URLSearchParams(window.location.search);
    const orgId = urlParams.get('orgId');
    const integrationId = urlParams.get('integrationId');
    const query = new URLSearchParams(window.location.search);

    const updateAudioFor = async () => {
        if (!audioFor) return toast.error("Please select something from list");

        if (!orgId) {
            toast.error("Organization ID not found. Please try signing up again.");
            history.push('/');
            return;
        }

        setIsLoading(true);

        try {
            if (audioFor === "Articles / Blogs") {
                await API.put('main', `/organisation/${orgId}/audio-article-customer`, {
                    headers: { "Content-Type": "application/json" },
                });
            }

            // Keep the tracked value to a fixed enum for clean Mixpanel segmentation —
            // free-text "Others" answers are still captured, just in a separate property.
            const isCustomAnswer = !audioForList.includes(audioFor);

            dispatch(addMixPanelEvent({
                orgId, body: {
                    eventName: "UPDATE_ORGANISATION_AUDIO_FOR",
                    eventData: {
                        audioFor: isCustomAnswer ? 'Other' : audioFor,
                        ...(isCustomAnswer && { audioFor_other_text: audioFor }),
                    }
                }
            }));

            dispatch(addMixPanelEvent({
                orgId, body: {
                    eventName: "ONBOARDING_STEP_COMPLETED",
                    eventData: {
                        step_name: 'audio_for',
                        step_number: 2,
                        value: isCustomAnswer ? 'Other' : audioFor,
                        ...(isCustomAnswer && { value_other_text: audioFor }),
                    }
                }
            }));

            // fire before redirect, not on dashboard load, so returning users don't inflate it
            trackGA4Event(ONBOARDING_STEP_COMPLETED, {
                step_number: 2,
                step_name: 'audio_for',
                value: isCustomAnswer ? 'Other' : audioFor,
            });

            const startedAt = Number(sessionStorage.getItem(onboardingStartedAtKey(orgId)));
            const timeToCompleteSec = startedAt ? Math.round((Date.now() - startedAt) / 1000) : undefined;
            const useCase = sessionStorage.getItem(onboardingUseCaseKey(orgId)) || undefined;

            dispatch(addMixPanelEvent({
                orgId, body: {
                    eventName: "ONBOARDING_COMPLETED",
                    eventData: {
                        steps_completed: 2,
                        use_case: useCase,
                        time_to_complete_sec: timeToCompleteSec,
                    }
                }
            }));

            sessionStorage.removeItem(onboardingStartedAtKey(orgId));
            sessionStorage.removeItem(onboardingUseCaseKey(orgId));

            // wait for GTM to confirm the tag fired (or its own timeout) before navigating away
            await trackGA4EventAsync(ONBOARDING_COMPLETED, {
                steps_completed: 2,
                use_case: useCase,
                time_to_complete_sec: timeToCompleteSec,
            });
            handleNextRoute();

        } catch (error) {
            console.error('Error updating audio preference:', error);
            toast.error('Something went wrong. Please try again.');
            setIsLoading(false);
        }
    };

    const handleNextRoute = () => {
        setAuthState('signedin');

        if (!query.has('plan_id') || query.get('plan_id') === 'free-plan') {
            if (integrationId) {
                history.push(`/organisation/${orgId}/integration/${integrationId}/dashboard`);
            } else {
                history.push(`/organisation/${orgId}/integration`);
            }
            return;
        }

        let path = new CustomURL(`/organisation/${orgId}/upgrade`);

        if (query.has('quantity')) {
            path.appendSearchParam('quantity', query.get('quantity'));
        }

        if (query.has('plan_id')) {
            path.appendSearchParam('plan_id', query.get('plan_id'));
        }

        window.location.href = path.href();
    };

    return (
        <div className="container-fluid position-relative" style={{ minHeight: '100vh' }}>
            <div className="d-lg-none" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: 4, background: 'var(--bs-primary)', zIndex: 10 }} />
            <div className="row" style={{ minHeight: "100vh" }}>
                <div className="col-12 d-flex flex-column justify-content-center align-items-center">
                    <div className="row mb-4">
                        <div className="col-12 text-center">
                            <img className="logo mb-4" src={logo} alt="logo" style={{ maxWidth: "200px" }} />
                        </div>
                    </div>

                    <div style={{ maxWidth: 520, width: '100%', padding: '0 16px' }}>
                        <div style={{ width: 150, height: 6, borderRadius: 10, background: 'var(--bs-border-color)', margin: '0 auto 20px' }}>
                            <div style={{ height: '100%', background: 'var(--bs-primary)', borderRadius: 10, transition: 'width 0.3s ease', width: '100%' }} />
                        </div>

                        <h4 className="fw-bold text-body-emphasis text-center mb-1">What are you creating audio for?</h4>
                        <p className="text-center mb-4" style={{ color: 'var(--bs-secondary-color)', fontSize: 14 }}>
                            This will help us personalize your experience in Listen2It.
                        </p>

                        <div className="onboarding-tags mb-3">
                            {audioForList.map((tagName, tagNameIndex) => (
                                <div key={`audio-for-${tagNameIndex}`} className="onboarding-tag">
                                    <TagPill
                                        label={tagName}
                                        selected={tagName === audioFor}
                                        onClick={() => setAudioFor(tagName)}
                                    />
                                </div>
                            ))}
                        </div>

                        <div className="mb-3">
                            <label>Others</label>
                            <input
                                onChange={(e) => setAudioFor(e.target.value)}
                                value={!audioForList.includes(audioFor) ? audioFor : ""}
                                placeholder="Your answer"
                                type="text"
                                className="form-control"
                            />
                        </div>

                        <button
                            type="button"
                            onClick={isLoading ? undefined : updateAudioFor}
                            className="btn btn-primary w-100"
                            style={{ height: 44 }}
                        >
                            {isLoading ? <Spinner color="white" /> : 'Continue'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
